import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { finalize } from "rxjs/operators";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { InputTextModule } from "primeng/inputtext";
import { TextareaModule } from "primeng/textarea";
import {
  AutoCompleteCompleteEvent,
  AutoCompleteModule,
  AutoCompleteSelectEvent,
} from "primeng/autocomplete";
import { TooltipModule } from "primeng/tooltip";
import { GroupDetail } from "../client-group-detail.component";
import { ClientGroupService } from "../../../../core/services/client-group.service";
import { ClientUserDto } from "../../../../core/models/client-group/client-user.dto";
import { JniToastrService } from "../../../../core/services/jni-toastr.service";

/** Maximum allowed length for the Group Name and Description fields. */
const MAX_FIELD_LENGTH = 256;

/** Characters permitted in a group name (letters, numbers, spaces and common punctuation). */
const GROUP_NAME_PATTERN = /^[a-zA-Z0-9 _\-&()./,'#@+]+$/;

@Component({
  selector: "jni-update-group-modal",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TextareaModule,
    AutoCompleteModule,
    TooltipModule,
  ],
  templateUrl: "./update-group-modal.component.html",
  styleUrls: ["./update-group-modal.component.scss"],
})
export class UpdateGroupModalComponent implements OnChanges {
  @Input() visible = false;
  @Input() group: GroupDetail | null = null;
  @Input() customerAccount = "";
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() groupUpdated = new EventEmitter<void>();

  readonly form = new FormGroup({
    groupName: new FormControl("", {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.maxLength(MAX_FIELD_LENGTH),
        Validators.pattern(GROUP_NAME_PATTERN),
      ],
    }),
    description: new FormControl("", {
      nonNullable: true,
      validators: [Validators.maxLength(MAX_FIELD_LENGTH)],
    }),
    userSearch: new FormControl<string | ClientUserDto>("", {
      nonNullable: true,
    }),
  });
  assignedUsers: string[] = [];
  /** Snapshot of the group's membership as loaded from the server, used to compute removals on save. */
  private _originalAssignedUsers: string[] = [];
  clientUsers: ClientUserDto[] = [];
  suggestions: ClientUserDto[] = [];
  saving = false;
  usersLoading = false;
  /** Emails for which a resend-invitation request is currently in progress. */
  readonly resendingEmails = new Set<string>();

  constructor(
    private _clientGroupService: ClientGroupService,
    private _toastr: JniToastrService,
    private _destroyRef: DestroyRef
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes["customerAccount"]) {
      this.loadClientUsers();
    }
    // Re-populate from the source group whenever the group reference changes or the
    // dialog is (re)opened, so any unsaved edits from a previous session are discarded.
    if (
      (changes["group"] || changes["visible"]) &&
      this.visible &&
      this.group
    ) {
      this.form.reset({
        groupName: this.group.groupName,
        description: this.group.description ?? "",
        userSearch: "",
      });
      this.assignedUsers = (this.group.users ?? []).map(u => u.email);
      this._originalAssignedUsers = [...this.assignedUsers];
      this.loadGroupUsers(this.group.groupId);
    }
    // Reload users each time the modal opens so the list is always fresh
    // and resilient to initial-load failures (e.g. server not yet ready).
    if (changes["visible"] && this.visible) {
      this.loadClientUsers();
    }
  }

  closeModal() {
    this.visibleChange.emit(false);
  }

  onVisibleChange(visible: boolean) {
    this.visibleChange.emit(visible);
  }

  searchUsers(event: AutoCompleteCompleteEvent) {
    const query = (event.query ?? "").trim();
    if (query.length < 2) {
      this.suggestions = [];
      return;
    }

    this._clientGroupService
      .searchClientUsers(this.customerAccount, query)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(users => {
        this.suggestions = users.filter(
          user => !this.assignedUsers.includes(user.email)
        );
      });
  }

  onUserSelect(event: AutoCompleteSelectEvent) {
    const user = event.value as ClientUserDto;
    this.addEmail(user.email);
  }

  addUser() {
    const value = this.form.controls.userSearch.value;
    if (typeof value === "string") {
      this.addEmail(value);
    }
  }

  removeUser(email: string) {
    this.assignedUsers = this.assignedUsers.filter(u => u !== email);
  }

  /**
   * Determines whether a resend-invitation request is in progress for the given email.
   * @param email The user email to check.
   * @returns `true` when a resend is in progress for the email; otherwise, `false`.
   */
  isResending(email: string): boolean {
    return this.resendingEmails.has(email);
  }

  /**
   * Triggers a resend of the invitation email for an assigned user. The icon is
   * disabled while the request is in flight to prevent duplicate submissions.
   * @param email The user email to resend the invitation to.
   */
  resendInvitation(email: string) {
    const groupId = this.group?.groupId;
    if (groupId == null || this.resendingEmails.has(email)) {
      return;
    }
    this.resendingEmails.add(email);
    this._clientGroupService
      .resendInvitation(email)
      .pipe(
        finalize(() => this.resendingEmails.delete(email)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this._toastr.success("Invitation email resent successfully.");
        },
        error: () => {
          this._toastr.error(
            "Unable to resend invitation email. Please try again."
          );
        },
      });
  }

  updateGroup() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const groupId = this.group?.groupId;
    if (groupId == null) {
      return;
    }
    const { groupName, description } = this.form.getRawValue();
    this.saving = true;
    this.form.disable();

    // Build a delta for the upsert: assigned users are sent as active additions, while users that
    // were members when the group loaded but have since been removed are sent as inactive removals.
    // Members absent from the payload are left untouched by the backend.
    const currentEmails = new Set(this.assignedUsers.map(e => e.toLowerCase()));
    const users = [
      ...this.assignedUsers.map(email => ({ email, isActive: true })),
      ...this._originalAssignedUsers
        .filter(email => !currentEmails.has(email.toLowerCase()))
        .map(email => ({ email, isActive: false })),
    ];

    this._clientGroupService
      .createOrUpdateGroup(this.customerAccount, {
        groupId,
        groupName: groupName.trim(),
        description: description.trim() || null,
        users,
      })
      .pipe(
        finalize(() => {
          this.saving = false;
          this.form.enable();
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this._toastr.success("Group updated successfully.");
          this.groupUpdated.emit();
        },
        error: (error: unknown) => {
          // Show the backend-provided validation message (e.g. duplicate group name) when present;
          // fall back to a generic message for transport/server failures. The form is left intact
          // so the user's entered details are preserved for another attempt.
          const message =
            error instanceof HttpErrorResponse
              ? "Unable to update group. Please try again."
              : error instanceof Error && error.message
                ? error.message
                : "Unable to update group. Please try again.";
          this._toastr.error(message);
        },
      });
  }

  private addEmail(raw: string) {
    const email = (raw ?? "").trim();
    if (!email) {
      this.form.controls.userSearch.setValue("");
      return;
    }
    if (!this.isValidEmail(email)) {
      this._toastr.warning("Please enter a valid email address.");
      return;
    }
    if (this.assignedUsers.includes(email)) {
      this._toastr.warning("This user is already assigned to the group.");
      this.form.controls.userSearch.setValue("");
      return;
    }

    // Enforce the single-client rule before adding: an email already associated with another client
    // cannot be assigned here. The backend owns the association, so it is the source of truth.
    this._clientGroupService
      .validateUserEmail(this.customerAccount, email)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this.assignedUsers.push(email);
          this.form.controls.userSearch.setValue("");
        },
        error: (error: unknown) => {
          const message =
            error instanceof HttpErrorResponse
              ? "Unable to validate the email. Please try again."
              : error instanceof Error && error.message
                ? error.message
                : "This email is already associated with another client.";
          this._toastr.error(message);
        },
      });
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private loadGroupUsers(groupId: number) {
    this.usersLoading = true;
    this._clientGroupService
      .getGroupById(groupId)
      .pipe(
        finalize(() => (this.usersLoading = false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: detail => {
          this.assignedUsers = (detail?.users ?? []).map(u => u.email);
          this._originalAssignedUsers = [...this.assignedUsers];
        },
      });
  }

  private loadClientUsers() {
    if (!this.customerAccount) return;
    this._clientGroupService
      .getClientUsers(this.customerAccount)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(users => (this.clientUsers = users));
  }
}
